import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTracking } from 'react-tracking';

import {
  Box,
  Grid,
  Heading,
  Text,
  useMount,
  useTheme,
} from '@simplywallst/ui-core';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';

import { fetchPortfolioList } from '@/components/DeprecatedPortfolio/redux/routines';
import If from '@/components/If';
import Tooltip from '@/components/Tooltip';

import { usePortfolios } from '@/hooks/portfolio/usePortfolio';

import useFetchEveryThirty from '../../hooks/useFetchEveryThirty';

import type { State as DashboardState } from '../../redux/reducers';
import { fetchDashboardMyMarketsAndIndustries } from '../../redux/routines';
import {
  getPortfolioEntitiesSelector,
  getPortfolioKeysSelector,
} from '../../redux/selectors';
import type { Entity } from '../../redux/selectors/myMarketsAndIndustriesSelectors';

import DeprecatedPortfolioItem from './DeprecatedPortfolioItem';
import MarketIndustryItem from './MarketIndustryItem';
import Skeleton from './Skeleton';
import { Card, Wrapper } from './styled';

interface Props {
  myMarketKeys: string[];
  myIndustryKeys: string[];
  myMarketsAndIndustriesLastUpdated: number | null;
  myMarketAndIndustryEntities: Record<string, Entity>;
}

const DeprecatedPortfolioAndMyMarkets = ({
  myMarketKeys,
  myIndustryKeys,
  myMarketAndIndustryEntities,
  myMarketsAndIndustriesLastUpdated,
}: Props) => {
  const theme = useTheme();

  const dispatch = useDispatch();
  const { Track } = useTracking({
    type: 'track',
    subject: 'mobile_portfolio_and_market_module',
  });
  const callback = useCallback(() => {
    dispatch(fetchDashboardMyMarketsAndIndustries.trigger());
  }, [dispatch]);
  const fetchStatus = useFetchEveryThirty(
    myMarketsAndIndustriesLastUpdated,
    callback
  );

  const { data: portfolios = [], isFetching: isFetchingPortfolios } =
    usePortfolios();

  const deprecatedKeys = useSelector(getPortfolioKeysSelector, shallowEqual);
  const deprecatedPortfolios = useSelector(
    getPortfolioEntitiesSelector,
    shallowEqual
  );
  const deprecatedPortfoliosStatus = useSelector(
    (state: { dashboard: DashboardState }) =>
      state.dashboard.portfoliosFetchStatus,
    shallowEqual
  );

  const isFetching =
    isFetchingPortfolios || deprecatedPortfoliosStatus === 'uninitialised';

  const newPortfolioKeys = portfolios.map((p) => p.id);

  useMount(() => {
    if (deprecatedPortfoliosStatus === 'uninitialised') {
      dispatch(fetchPortfolioList.trigger());
    }
  });

  const hasOnlyDeprecatedPortfolios =
    newPortfolioKeys.length === 0 && deprecatedKeys.length > 0;

  if (isFetching) {
    return <Skeleton />;
  }

  return (
    <Track>
      <Wrapper data-cy-id="dashboard-mobile-portfolios-table">
        {deprecatedKeys.length > 0 && (
          <Card>
            <Grid
              height="88px"
              templateColumns="100px"
              padding="x1"
              templateRows="auto auto"
              alignContent="space-between"
              borderRadius="x0_5"
            >
              <Heading as="h1" typography="xSmallStrong">
                Legacy Portfolios
              </Heading>
              <Grid templateColumns="min-content">
                <Tooltip
                  title={
                    <Box maxWidth="260px" padding="x1">
                      <Text
                        textAlign="center"
                        typography="tiny"
                        color="spaceGray"
                      >
                        You can still access your legacy portfolios from this
                        table or from the My Portfolios page.
                      </Text>
                    </Box>
                  }
                  light
                  placement="top"
                  zIndex={99999}
                >
                  <Grid cursor="help">
                    <InformationSolid fill={theme.color.whiteRGBA(0.5)} />
                  </Grid>
                </Tooltip>
              </Grid>
            </Grid>
          </Card>
        )}

        {deprecatedKeys.map((key, index) => {
          const deprecatedPortfolio = deprecatedPortfolios[key];
          if (!deprecatedPortfolio) return null;
          return (
            <DeprecatedPortfolioItem
              key={`portfolio-item-${deprecatedPortfolio.name}-${key}`}
              id={key}
              index={index}
              name={deprecatedPortfolio.name}
              canonicalUrl={deprecatedPortfolio.canonicalUrl}
              isWatchlist={deprecatedPortfolio.isWatchlist}
              portfolioWithNoTransactions={
                deprecatedPortfolio.portfolioWithNoTransactions
              }
              lastAnalysisUpdate={deprecatedPortfolio.lastAnalysisUpdate}
              dateGenerated={deprecatedPortfolio.dateGenerated}
              holdingsCount={deprecatedPortfolio.holdingsCount}
              return7D={deprecatedPortfolio.return7D}
              return7DFormatted={deprecatedPortfolio.return7DFormatted}
              totalValueFormatted={deprecatedPortfolio.totalValueFormatted}
              totalGainFormatted={deprecatedPortfolio.totalGainFormatted}
              totalDividendReturn={deprecatedPortfolio.totalDividendReturn}
              totalDividendReturnFormatted={
                deprecatedPortfolio.totalDividendReturnFormatted
              }
            />
          );
        })}
        <If match={fetchStatus === 'fetching'}>
          <Skeleton outline />
        </If>
        <If match={fetchStatus === 'idle' && hasOnlyDeprecatedPortfolios}>
          {myMarketKeys.map((key, index) => {
            const marketItem = myMarketAndIndustryEntities[key];

            return (
              <MarketIndustryItem
                key={`market-item-${marketItem.marketISO}-${key}`}
                id={key}
                index={index + deprecatedKeys.length}
                slug={marketItem.marketISO}
                name={`${marketItem.marketISO.toUpperCase()} Market`}
                return7D={marketItem.return7D}
                return7DFormatted={marketItem.return7DFormatted}
                return1YrAbs={marketItem.return1YrAbs}
                return1YrAbsFormatted={marketItem.return1YrAbsFormatted}
              />
            );
          })}
          {myIndustryKeys.map((key, index) => {
            const industryItem = myMarketAndIndustryEntities[key];

            return (
              <MarketIndustryItem
                key={`industry-item-${industryItem.industrySlug}-${key}`}
                id={key}
                index={index + deprecatedKeys.length + myMarketKeys.length}
                slug={industryItem.industrySlug}
                name={`${industryItem.marketISO.toUpperCase()} ${
                  industryItem.industryName
                }`}
                return7D={industryItem.return7D}
                return7DFormatted={industryItem.return7DFormatted}
                return1YrAbs={industryItem.return1YrAbs}
                return1YrAbsFormatted={industryItem.return1YrAbsFormatted}
              />
            );
          })}
        </If>
      </Wrapper>
    </Track>
  );
};

export default DeprecatedPortfolioAndMyMarkets;
